import React, { useState, useEffect } from 'react';
import Loading from 'components/commons/loading/Loading';
import { useRouting } from 'hooks/useRouting';
import {
  getNotificationSetting,
  GetNotificationSettingResult,
  updateNotificationSetting,
  UpdateNotificationSetting,
} from 'config/api';
import {
  Error,
  Errors,
} from 'components/features/target-achievement-support/components/commons/Error';
import { toNumber } from 'components/features/target-achievement-support/utils/utils';

const AsstsChangeNotificationItem = ({
  notificationSetting,
  registNotificationSetting,
}: {
  notificationSetting: GetNotificationSettingResult;
  registNotificationSetting: (val: number) => void;
}): React.ReactElement => {
  const routing = useRouting();

  const [changeRate, setChangeRate] = useState<number | null>(
    notificationSetting.AssetFluctuationThresholdPer
  );

  const isChangeRateInputError = changeRate === null;
  const isChangeRateValueError =
    changeRate !== null && (changeRate < 1 || 100 < changeRate);
  const [errorInfo, setErrorInfo] = useState<SetNumError | null>(null);
  const inputErrorCheck = isChangeRateInputError || isChangeRateValueError;
  const [isInputError, setIsInputError] = useState<boolean>(inputErrorCheck);

  useEffect(() => {
    setIsInputError(inputErrorCheck);
    const e = isChangeRateInputError
      ? changeRateInputError
      : isChangeRateValueError
      ? changeRateValueError
      : null;
    setErrorInfo(e);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeRate]);

  const regist = async () => {
    if (changeRate === null) return;
    await registNotificationSetting(changeRate);
    routing.backRouting(true);
  };

  type SetNumError = {
    tag: 'ChangeRateInputError' | 'ChangeRateValueError';
    description: string;
  };

  const changeRateInputError: SetNumError = {
    tag: 'ChangeRateInputError' as const,
    description: '変動率は1～100%の値で入力して下さい。',
  };

  const changeRateValueError: SetNumError = {
    tag: 'ChangeRateValueError' as const,
    description: '変動率は1～100%の値で入力して下さい。',
  };

  return (
    <div className={`p-4 flex flex-col bg-white`}>
      <div className={`text-lg font-bold`}>大きな資産変動をお知らせ</div>
      <div className={`mt-2 text-sm`}>
        資産が大きく変動した時に日次レポートとLINEでお知らせします。この画面から、お知らせが表示される変動率が変更できます。
      </div>
      <div
        className="mt-4 mr-auto items-baseline grid w-3/4"
        style={{ gridTemplateColumns: '1fr auto' }}>
        <input
          className={`border rounded-lg text-bluegray-700 font-bold text-xl px-3 py-1 text-right w-full ${
            isInputError ? 'bg-red-150 border-tradewell' : 'border-bluegray-300'
          }`}
          type="number"
          value={changeRate === null ? '' : changeRate}
          onChange={(e): void => {
            if (e.target.value !== '') {
              const num = toNumber(e.target.value);
              setChangeRate(num !== null ? num : 0);
            } else {
              setChangeRate(null);
            }
          }}
        />
        <div className={`text-sm ml-1`}>
          <span className={`text-lg pr-1`}>％</span>変動したらお知らせ
        </div>
      </div>
      {isInputError && errorInfo !== null && (
        <Errors className="mt-1">
          <Error
            {...{
              error: errorInfo,
              errorToString: (e: SetNumError): string => {
                return e.description;
              },
            }}
          />
        </Errors>
      )}
      <button
        className={`mt-8 bg-tradewell rounded-lg shadow text-white py-2 text-center flex flex-row items-center gap-2 justify-center text-base font-bold ${
          isInputError ? 'opacity-20' : ''
        }`}
        disabled={isInputError}
        onClick={() => regist()}>
        決定
      </button>
      <button
        className={`mt-4 p-2 rounded-lg border border-bluegray-400 text-bluegray-700 bg-gray-50 font-bold flex flex-row items-center justify-center text-base`}
        onClick={(): void => routing.backRouting(true)}>
        戻る
      </button>
    </div>
  );
};

export const AsstsChangeNotification: React.FC = () => {
  const [notificationSetting, setNotificationSetting] = useState<
    GetNotificationSettingResult | undefined
  >();

  useEffect(() => {
    (async () => {
      const res = await getNotificationSetting();
      setNotificationSetting(res);
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!notificationSetting)
    return (
      <Loading
        loading={true}
        style={{
          backgroundColor: '#edf2f7',
          alignItems: 'normal',
          paddingTop: '200px',
          top: '95',
          left: '0',
        }}
      />
    );

  const registNotificationSetting = async (val: number) => {
    const prams = {
      TimelyDisclosureBySymbol: notificationSetting.TimelyDisclosureBySymbol,
      AssetFluctuationThresholdPer: val,
      EconomicIndicatorsOnToday: notificationSetting.EconomicIndicatorsOnToday,
      NewDepositAndWithdrawal: notificationSetting.NewDepositAndWithdrawal,
      NewTrade: notificationSetting.NewTrade,
    } as UpdateNotificationSetting;

    await updateNotificationSetting(prams);
  };

  return (
    <AsstsChangeNotificationItem
      {...{
        notificationSetting,
        registNotificationSetting,
      }}
    />
  );
};

export default AsstsChangeNotification;
